import React, { useState, useEffect } from 'react';
import { getDrivers, getAbsences, addAbsence, getDelays, addDelay, getBonuses, addBonus } from '../api/api';
import { format } from 'date-fns';

function HRManagement({ user }) {
  const [activeTab, setActiveTab] = useState('absences');
  const [employees, setEmployees] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [delays, setDelays] = useState([]);
  const [bonuses, setBonuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  // États des formulaires
  const [absenceForm, setAbsenceForm] = useState({
    employee_id: '',
    type: 'maladie',
    start_date: format(new Date(), 'yyyy-MM-dd'),
    end_date: format(new Date(), 'yyyy-MM-dd'),
    reason: ''
  });

  const [delayForm, setDelayForm] = useState({
    employee_id: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    minutes: '',
    reason: ''
  });

  const [bonusForm, setBonusForm] = useState({
    employee_id: '',
    type: 'tri',
    amount: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    reason: ''
  });

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const empResponse = await getDrivers();
      setEmployees(empResponse.data);

      if (activeTab === 'absences') {
        const response = await getAbsences();
        setAbsences(response.data);
      } else if (activeTab === 'delays') {
        const response = await getDelays();
        setDelays(response.data);
      } else if (activeTab === 'bonuses') {
        const response = await getBonuses();
        setBonuses(response.data);
      }
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAbsenceSubmit = async (e) => {
    e.preventDefault();
    try {
      await addAbsence(absenceForm);
      alert('Absence enregistrée avec succès !');
      setShowForm(false);
      setAbsenceForm({
        employee_id: '',
        type: 'maladie',
        start_date: format(new Date(), 'yyyy-MM-dd'),
        end_date: format(new Date(), 'yyyy-MM-dd'),
        reason: ''
      });
      fetchData();
    } catch (error) {
      alert('Erreur: ' + (error.response?.data?.error || 'Une erreur est survenue'));
    }
  };

  const handleDelaySubmit = async (e) => {
    e.preventDefault();
    try {
      await addDelay(delayForm);
      alert('Retard enregistré avec succès !');
      setShowForm(false);
      setDelayForm({
        employee_id: '',
        date: format(new Date(), 'yyyy-MM-dd'),
        minutes: '',
        reason: ''
      });
      fetchData();
    } catch (error) {
      alert('Erreur: ' + (error.response?.data?.error || 'Une erreur est survenue'));
    }
  };

  const handleBonusSubmit = async (e) => {
    e.preventDefault();
    try {
      await addBonus(bonusForm);
      alert('Prime enregistrée avec succès !');
      setShowForm(false);
      setBonusForm({
        employee_id: '',
        type: 'tri',
        amount: '',
        date: format(new Date(), 'yyyy-MM-dd'),
        reason: ''
      });
      fetchData();
    } catch (error) {
      alert('Erreur: ' + (error.response?.data?.error || 'Une erreur est survenue'));
    }
  };

  const absenceTypes = {
    maladie: 'Arrêt maladie',
    paternite: 'Congé paternité',
    maternite: 'Congé maternité',
    accident_travail: 'Accident de travail',
    conge_paye: 'Congé payé',
    autre: 'Autre'
  };

  const bonusTypes = {
    tri: 'Prime de tri',
    performance: 'Prime de performance',
    ponctualite: 'Prime de ponctualité',
    autre: 'Autre'
  };

  if (loading) {
    return <div className="container"><div className="loading">Chargement...</div></div>;
  }

  return (
    <div className="container">
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
          <h2>👥 Gestion RH</h2>
          <button className="btn-primary" onClick={() => setShowForm(!showForm)}>
            {showForm ? 'Annuler' : '+ Ajouter'}
          </button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem', borderBottom: '2px solid #eee' }}>
          <button
            onClick={() => { setActiveTab('absences'); setShowForm(false); }}
            style={{
              padding: '0.75rem 1.5rem',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              borderBottom: activeTab === 'absences' ? '3px solid #B8472E' : 'none',
              fontWeight: activeTab === 'absences' ? 'bold' : 'normal',
              color: activeTab === 'absences' ? '#B8472E' : '#666'
            }}
          >
            Absences
          </button>
          <button
            onClick={() => { setActiveTab('delays'); setShowForm(false); }}
            style={{
              padding: '0.75rem 1.5rem',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              borderBottom: activeTab === 'delays' ? '3px solid #B8472E' : 'none',
              fontWeight: activeTab === 'delays' ? 'bold' : 'normal',
              color: activeTab === 'delays' ? '#B8472E' : '#666'
            }}
          >
            Retards
          </button>
          <button
            onClick={() => { setActiveTab('bonuses'); setShowForm(false); }}
            style={{
              padding: '0.75rem 1.5rem',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              borderBottom: activeTab === 'bonuses' ? '3px solid #B8472E' : 'none',
              fontWeight: activeTab === 'bonuses' ? 'bold' : 'normal',
              color: activeTab === 'bonuses' ? '#B8472E' : '#666'
            }}
          >
            Primes
          </button>
        </div>

        {/* Forms */}
        {showForm && activeTab === 'absences' && (
          <form onSubmit={handleAbsenceSubmit} style={{ marginBottom: '2rem', padding: '1.5rem', background: '#f8f9fa', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: '1rem' }}>Nouvelle absence</h3>
            <div className="form-group">
              <label>Employé</label>
              <select value={absenceForm.employee_id} onChange={(e) => setAbsenceForm({ ...absenceForm, employee_id: e.target.value })} required>
                <option value="">Sélectionner un employé</option>
                {employees.map((emp) => (
                  <option key={emp.id} value={emp.id}>{emp.firstname} {emp.lastname}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Type d'absence</label>
              <select value={absenceForm.type} onChange={(e) => setAbsenceForm({ ...absenceForm, type: e.target.value })} required>
                {Object.entries(absenceTypes).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div className="form-group">
                <label>Date de début</label>
                <input type="date" value={absenceForm.start_date} onChange={(e) => setAbsenceForm({ ...absenceForm, start_date: e.target.value })} required />
              </div>
              <div className="form-group">
                <label>Date de fin</label>
                <input type="date" value={absenceForm.end_date} onChange={(e) => setAbsenceForm({ ...absenceForm, end_date: e.target.value })} required />
              </div>
            </div>
            <div className="form-group">
              <label>Raison</label>
              <textarea value={absenceForm.reason} onChange={(e) => setAbsenceForm({ ...absenceForm, reason: e.target.value })} rows="3" />
            </div>
            <button type="submit" className="btn-success">Enregistrer</button>
          </form>
        )}

        {showForm && activeTab === 'delays' && (
          <form onSubmit={handleDelaySubmit} style={{ marginBottom: '2rem', padding: '1.5rem', background: '#f8f9fa', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: '1rem' }}>Nouveau retard</h3>
            <div className="form-group">
              <label>Employé</label>
              <select value={delayForm.employee_id} onChange={(e) => setDelayForm({ ...delayForm, employee_id: e.target.value })} required>
                <option value="">Sélectionner un employé</option>
                {employees.map((emp) => (
                  <option key={emp.id} value={emp.id}>{emp.firstname} {emp.lastname}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Date</label>
              <input type="date" value={delayForm.date} onChange={(e) => setDelayForm({ ...delayForm, date: e.target.value })} required />
            </div>
            <div className="form-group">
              <label>Durée (minutes)</label>
              <input type="number" value={delayForm.minutes} onChange={(e) => setDelayForm({ ...delayForm, minutes: e.target.value })} required />
            </div>
            <div className="form-group">
              <label>Raison</label>
              <textarea value={delayForm.reason} onChange={(e) => setDelayForm({ ...delayForm, reason: e.target.value })} rows="3" />
            </div>
            <button type="submit" className="btn-success">Enregistrer</button>
          </form>
        )}

        {showForm && activeTab === 'bonuses' && (
          <form onSubmit={handleBonusSubmit} style={{ marginBottom: '2rem', padding: '1.5rem', background: '#f8f9fa', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: '1rem' }}>Nouvelle prime</h3>
            <div className="form-group">
              <label>Employé</label>
              <select value={bonusForm.employee_id} onChange={(e) => setBonusForm({ ...bonusForm, employee_id: e.target.value })} required>
                <option value="">Sélectionner un employé</option>
                {employees.map((emp) => (
                  <option key={emp.id} value={emp.id}>{emp.firstname} {emp.lastname}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Type de prime</label>
              <select value={bonusForm.type} onChange={(e) => setBonusForm({ ...bonusForm, type: e.target.value })} required>
                {Object.entries(bonusTypes).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div className="form-group">
                <label>Montant (€)</label>
                <input type="number" step="0.01" value={bonusForm.amount} onChange={(e) => setBonusForm({ ...bonusForm, amount: e.target.value })} required />
              </div>
              <div className="form-group">
                <label>Date</label>
                <input type="date" value={bonusForm.date} onChange={(e) => setBonusForm({ ...bonusForm, date: e.target.value })} required />
              </div>
            </div>
            <div className="form-group">
              <label>Raison</label>
              <textarea value={bonusForm.reason} onChange={(e) => setBonusForm({ ...bonusForm, reason: e.target.value })} rows="3" />
            </div>
            <button type="submit" className="btn-success">Enregistrer</button>
          </form>
        )}

        {/* Tables */}
        {activeTab === 'absences' && (
          <>
            <h3 style={{ marginBottom: '1rem' }}>Liste des absences</h3>
            {absences.length === 0 ? (
              <p>Aucune absence enregistrée.</p>
            ) : (
              <div className="table-wrap"><table>
                <thead>
                  <tr>
                    <th>Employé</th>
                    <th>Type</th>
                    <th>Date début</th>
                    <th>Date fin</th>
                    <th>Raison</th>
                    <th>Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {absences.map((absence) => (
                    <tr key={absence.id}>
                      <td>{absence.firstname} {absence.lastname}</td>
                      <td>{absenceTypes[absence.type]}</td>
                      <td>{format(new Date(absence.start_date), 'dd/MM/yyyy')}</td>
                      <td>{format(new Date(absence.end_date), 'dd/MM/yyyy')}</td>
                      <td>{absence.reason}</td>
                      <td>{absence.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            )}
          </>
        )}

        {activeTab === 'delays' && (
          <>
            <h3 style={{ marginBottom: '1rem' }}>Liste des retards</h3>
            {delays.length === 0 ? (
              <p>Aucun retard enregistré.</p>
            ) : (
              <div className="table-wrap"><table>
                <thead>
                  <tr>
                    <th>Employé</th>
                    <th>Date</th>
                    <th>Durée (min)</th>
                    <th>Raison</th>
                  </tr>
                </thead>
                <tbody>
                  {delays.map((delay) => (
                    <tr key={delay.id}>
                      <td>{delay.firstname} {delay.lastname}</td>
                      <td>{format(new Date(delay.date), 'dd/MM/yyyy')}</td>
                      <td>{delay.minutes}</td>
                      <td>{delay.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            )}
          </>
        )}

        {activeTab === 'bonuses' && (
          <>
            <h3 style={{ marginBottom: '1rem' }}>Liste des primes</h3>
            {bonuses.length === 0 ? (
              <p>Aucune prime enregistrée.</p>
            ) : (
              <div className="table-wrap"><table>
                <thead>
                  <tr>
                    <th>Employé</th>
                    <th>Type</th>
                    <th>Montant (€)</th>
                    <th>Date</th>
                    <th>Raison</th>
                  </tr>
                </thead>
                <tbody>
                  {bonuses.map((bonus) => (
                    <tr key={bonus.id}>
                      <td>{bonus.firstname} {bonus.lastname}</td>
                      <td>{bonusTypes[bonus.type]}</td>
                      <td>{bonus.amount.toFixed(2)} €</td>
                      <td>{format(new Date(bonus.date), 'dd/MM/yyyy')}</td>
                      <td>{bonus.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default HRManagement;
